import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { getParamAsInt } from "../../../../app/utils";
import { Loading } from "../../../../components/Loading";
import { SimpleOption } from "../../../../components/input/SimpleListbox";
import Select from "../../../../components/input/Select";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import { ReportLevel, AdminReportsResponse } from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import {
	PromptUsageAndPerformanceByLevelRow,
	useLazyGetPromptOptionsForReportsQuery,
	useLazyPromptUsageAndPerformanceByLevelQuery,
} from "./adminPromptReportApiSlice";
import { PromptUsageAndPerformanceByLevelTable } from "./PromptUsageAndPerformanceByLevelTable";
import PromptUsageAndPerformanceByLevelChart from "./PromptUsageAndPerformanceByLevelChart";

export default function PromptUsageAndPerformanceByLevel() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [queryParams, setSearchParams] = useSearchParams();
	const promptIdParam = getParamAsInt(queryParams, "promptId");

	const [data, setData] = useState<AdminReportsResponse<PromptUsageAndPerformanceByLevelRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [promptOptions, setPromptOptions] = useState<SimpleOption[]>([]);
	const [promptFilter, setPromptFilter] = useState<number | undefined>(promptIdParam);

	const [callPromptResults] = useLazyPromptUsageAndPerformanceByLevelQuery();
	const [callPromptOptions] = useLazyGetPromptOptionsForReportsQuery();

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (!promptFilter) return;
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callPromptResults({
			promptId: promptFilter,
			districtId: districtFilter === 0 ? undefined : districtFilter,
			schoolId: schoolFilter === 0 ? undefined : schoolFilter,
		})
			.unwrap()
			.then((payload) => {
				setData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
				setReportLevel(getReportLevel(districtFilter, schoolFilter));
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});
	}, [districtFilter, schoolFilter, promptFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
			promptFilter,
		});
	}, [districtFilter, schoolFilter, promptFilter]);

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent unnecessary calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callPromptOptions(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true
		)
			.unwrap()
			.then((prompts) => {
				const options = prompts.map(
					(p) => ({ value: p.id, label: p.name }) as SimpleOption
				);

				setPromptOptions(options);

				// Set the promptFilter to the first in the list if the filter isn't an option or is not populated from promptIdParam
				if (!promptFilter || !options.some((p) => p.value === promptFilter)) {
					setPromptFilter(Number(options[0]?.value));
				}

				// If there are no prompt options and a promptFilter has not been set, set an empty data state to stop loading to show an empty table
				if (options.length === 0 && !promptFilter) {
					setData({ rows: [], totals: {} as PromptUsageAndPerformanceByLevelRow });
				}
			});
	}, [districtFilter, schoolFilter]);

	const instructionByLevel = useMemo(() => {
		if (reportLevel === ReportLevel.State) {
			return "pre-packaged";
		} else if (reportLevel === ReportLevel.District) {
			return "pre-packaged and district";
		} else if (reportLevel === ReportLevel.School) {
			return "pre-packaged, district and school";
		}
	}, [reportLevel]);

	return (
		<section>
			<h1>
				{reportLevel} Prompt Usage and Performance by {reportSubLevelName(reportLevel)}
			</h1>
			<p className="mt-4 print:hidden">{`The Prompt Usage and Performance Report shows the number of essay and draft submissions, average number of drafts per essay and the average trait and total scores for each ${reportSubLevelName(reportLevel).toLowerCase()} that has written to the selected prompt. All ${instructionByLevel} prompts that have been written to are included in the prompt filter. ${schoolOptions.length > 1 || districtOptions.length > 1 ? `Use the filters below to view data for that selection.` : ""} Click the three dots to print or download.`}</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end gap-x-3">
							<div className="flex gap-3 items-end print:hidden flex-wrap">
								<DistrictSchoolFilters
									districtOptions={districtOptions}
									schoolOptions={schoolOptions}
									districtFilter={districtFilter}
									schoolFilter={schoolFilter}
									setDistrictFilter={setDistrictFilter}
									setSchoolFilter={setSchoolFilter}
								/>
								{promptOptions.length > 0 && (
									<Select
										value={promptFilter}
										setValue={(v) => setPromptFilter(v as number)}
										label="Prompt"
										simpleListBoxId="promptFilter"
										options={promptOptions}
										fieldClasses="w-full md:flex-1"
										simpleListBoxClasses="w-96"
									/>
								)}
							</div>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={data.rows.length !== 0}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="prompt-results"
								downloadExcelLink={`${aspUrl}/AdminReports/PromptResultsExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/PromptResultsCsv?${queryString}`}
							/>
						</div>

						<PromptUsageAndPerformanceByLevelTable
							className="mt-4"
							level={reportLevel}
							data={data}
							hideRows={hideRows}
							setDistrictFilter={setDistrictFilter}
							setSchoolFilter={setSchoolFilter}
						/>
						<PromptUsageAndPerformanceByLevelChart
							level={reportLevel}
							data={data.rows}
							hasData={data.rows.length > 0}
						/>
					</>
				)}
			</div>
		</section>
	);
}
